import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Target, Plus, Search, TrendingUp, AlertTriangle, CheckCircle2,
  Trash2, RefreshCw, Pencil, Trophy, BarChart3, Clock
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ExportButton } from '../components/shared/ExportButton';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const GOAL_TYPES = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'lead_count', label: 'Lead Count' },
  { value: 'appointments', label: 'Appointments' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'email_open_rate', label: 'Email Open Rate' },
  { value: 'social_reach', label: 'Social Reach' },
  { value: 'ticket_resolution', label: 'Ticket Resolution' },
  { value: 'nps_score', label: 'NPS Score' },
];

const PERIODS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];

const StatCard = ({ icon: Icon, label, value, color, suffix = '' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}{suffix}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function GoalTrackingPage() {
  const [goals, setGoals] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [form, setForm] = useState({ title: '', goal_type: 'revenue', target_value: 0, current_value: 0, period: 'monthly', start_date: '', end_date: '', assigned_name: '' });

  const fetchData = useCallback(async () => {
    try {
      const [g, d] = await Promise.all([
        axios.get(`${API}/api/goals`),
        axios.get(`${API}/api/goals/dashboard`),
      ]);
      setGoals(g.data.goals || []);
      setDashboard(d.data);
    } catch { toast.error('Failed to load goals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API}/api/goals`, form);
      toast.success('Goal created!');
      setShowCreate(false);
      setForm({ title: '', goal_type: 'revenue', target_value: 0, current_value: 0, period: 'monthly', start_date: '', end_date: '', assigned_name: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleUpdate = async () => {
    if (!editGoal) return;
    try {
      await axios.put(`${API}/api/goals/${editGoal.id}`, {
        current_value: editGoal.current_value,
        target_value: editGoal.target_value,
        status: editGoal.current_value >= editGoal.target_value ? 'achieved' : 'active',
      });
      toast.success('Goal updated!');
      setEditGoal(null);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deleteGoal = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API}/api/goals/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const filtered = goals.filter(g =>
    !search || g.title?.toLowerCase().includes(search.toLowerCase()) || g.goal_type?.toLowerCase().includes(search.toLowerCase())
  );

  const getProgressColor = (p) => p >= 100 ? 'text-emerald-600' : p >= 50 ? 'text-blue-600' : 'text-amber-600';

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="goal-tracking-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Goal Tracking<PageInfoTooltip pageKey="goal-tracking" /></h1>
          <p className="text-gray-medium text-sm mt-1">Set, track, and achieve your business goals</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            data={goals.map(g => ({ title: g.title, type: g.goal_type, target: g.target_value, current: g.current_value, status: g.status, period: g.period }))}
            filename="goals"
            columns={['title', 'type', 'target', 'current', 'status', 'period']}
          />
          <Button onClick={() => setShowCreate(true)} data-testid="create-goal-btn">
            <Plus className="h-4 w-4 mr-1" /> New Goal
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Target} label="Total Goals" value={dashboard.total || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={TrendingUp} label="Active" value={dashboard.active || 0} color="bg-amber-50 text-amber-600" />
        <StatCard icon={Trophy} label="Achieved" value={dashboard.achieved || 0} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={BarChart3} label="Avg Progress" value={dashboard.avg_progress || 0} color="bg-purple-50 text-purple-600" suffix="%" />
      </div>

      {dashboard.at_risk > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-700">{dashboard.at_risk} goal(s) at risk (below 50% progress)</span>
        </div>
      )}

      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search goals..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="goal-search" />
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-medium">
              <Target className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No goals yet. Create your first one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(goal => {
                const progress = goal.progress || 0;
                const isAchieved = goal.status === 'achieved' || progress >= 100;
                const typeLabel = GOAL_TYPES.find(t => t.value === goal.goal_type)?.label || goal.goal_type;
                return (
                  <div key={goal.id} className={cn("p-4 rounded-lg border transition-colors", isAchieved ? "border-emerald-200 bg-emerald-50/30" : "border-gray-border hover:bg-gray-50")} data-testid={`goal-card-${goal.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-sm text-text-primary">{goal.title}</h4>
                          <Badge className="text-[10px] bg-blue-100 text-blue-700">{typeLabel}</Badge>
                          <Badge className="text-[10px] bg-gray-100 text-gray-600 capitalize">{goal.period}</Badge>
                          {isAchieved && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Achieved</Badge>}
                        </div>
                        {goal.assigned_name && <p className="text-xs text-gray-medium mt-0.5">Assigned to: {goal.assigned_name}</p>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditGoal({ ...goal })}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteGoal(goal.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-medium">{goal.current_value?.toLocaleString()} / {goal.target_value?.toLocaleString()}</span>
                        <span className={cn("text-sm font-semibold", getProgressColor(progress))}>{progress}%</span>
                      </div>
                      <Progress value={Math.min(progress, 100)} className="h-2" />
                    </div>
                    {(goal.start_date || goal.end_date) && (
                      <div className="flex items-center gap-1 mt-2 text-[10px] text-gray-400">
                        <Clock className="h-3 w-3" />
                        <span>{goal.start_date || '?'} — {goal.end_date || '?'}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Goal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="goal-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={form.goal_type} onValueChange={v => setForm(f => ({ ...f, goal_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{GOAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Period</Label>
                <Select value={form.period} onValueChange={v => setForm(f => ({ ...f, period: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Target Value</Label><Input type="number" min={0} value={form.target_value} onChange={e => setForm(f => ({ ...f, target_value: Number(e.target.value) }))} /></div>
              <div><Label>Current Value</Label><Input type="number" min={0} value={form.current_value} onChange={e => setForm(f => ({ ...f, current_value: Number(e.target.value) }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} /></div>
            </div>
            <div><Label>Assigned To</Label><Input value={form.assigned_name} onChange={e => setForm(f => ({ ...f, assigned_name: e.target.value }))} placeholder="Person or team" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-goal-btn">Create Goal</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Progress Dialog */}
      <Dialog open={!!editGoal} onOpenChange={() => setEditGoal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Update Progress</DialogTitle></DialogHeader>
          {editGoal && (
            <div className="space-y-3">
              <p className="text-sm font-medium">{editGoal.title}</p>
              <div><Label>Current Value</Label><Input type="number" min={0} value={editGoal.current_value} onChange={e => setEditGoal(g => ({ ...g, current_value: Number(e.target.value) }))} data-testid="edit-current-value" /></div>
              <div><Label>Target Value</Label><Input type="number" min={0} value={editGoal.target_value} onChange={e => setEditGoal(g => ({ ...g, target_value: Number(e.target.value) }))} /></div>
              <Progress value={Math.min(editGoal.current_value / Math.max(editGoal.target_value, 1) * 100, 100)} className="h-2" />
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditGoal(null)}>Cancel</Button><Button onClick={handleUpdate} data-testid="update-goal-btn">Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
